import { useEffect, useState } from 'react';
import { Table, Button, Modal, Collapse, Card, Typography, Tag, Switch } from 'antd';
import { PlusOutlined } from '@ant-design/icons';
import { useSnackbar } from 'notistack';
import MatchandMergeDetails from './MatchandMergeDetails';
import { getMatchRuleByEntityId, updateActiveMatchStatus } from "../../../../api's/DataModelApi";
import LoadingIcon from '../../../../reusable-component/LoadingIcon';

const { Panel } = Collapse;

const MatchandMerge = ({ entityId, attributes }) => {
  const { enqueueSnackbar } = useSnackbar();

  const [open, setOpen] = useState(false);
  const [matchRulesData, setMatchRulesData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [editingData, setEditingData] = useState(null);
  const [openRow, setOpenRow] = useState(null);

  const toggleRow = (id) => {
    setOpenRow(openRow === id ? null : id);
  };

  const handleCreateNew = () => {
    setEditingData(null);
    setOpen(true);
  };

  const handleEdit = (rule) => {
    setEditingData(rule);
    setOpen(true);
  };

  const handleActiveStatus = async (id) => {
    setLoading(true);
    try {
      console.log({ id });
      const response = await updateActiveMatchStatus(id);
      if (response.status === 200) {
        enqueueSnackbar('Status update!', { variant: 'success' });
        await fetchMatchRules();
      }
    } catch (error) {
      enqueueSnackbar('Failed to update status!', { variant: 'error' });
    } finally {
      setLoading(false);
    }
  };

  const fetchMatchRules = async () => {
    setLoading(true);
    try {
      const response = await getMatchRuleByEntityId(entityId);
      if (response.status === 200) {
        setMatchRulesData(response.data);
      }
    } catch (error) {
      enqueueSnackbar('Failed to fetch match rules', { variant: 'error' });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchMatchRules();
  }, [entityId]);

  // ---------------- COLUMNS ----------------
  const columns = [
    {
      title: 'Name',
      dataIndex: 'matchRuleName',
      sorter: (a, b) => (a.matchRuleName || '').localeCompare(b.matchRuleName || ''),
      sortDirections: ['ascend', 'descend'],
      defaultSortOrder: 'ascend',
    },
    {
      title: 'Strategy',
      dataIndex: 'ruleStrategy',
      render: (val) => val.replace('_', ' '),
    },
    {
      title: 'Overall Threshold',
      dataIndex: 'ruleOverallThreshold',
      render: (val) => (val ? `${val}%` : '-'),
    },
    {
      title: 'Description',
      dataIndex: 'matchRuleDescription',
      ellipsis: true,
    },
    // {
    //   title: 'Conditions',
    //   render: (_, record) => (
    //     <Button type="link" onClick={() => toggleRow(record.id)}>
    //       {openRow === record.id ? 'Hide Conditions' : 'Show Conditions'}
    //     </Button>
    //   ),
    // },
    // {
    //   title: 'Status',
    //   dataIndex: 'ruleStatus',
    //   render: (val) => (val ? <Tag color="green">Active</Tag> : <Tag color="red">Inactive</Tag>),
    // },
    {
      title: 'Active',
      dataIndex: 'ruleStatus',
      key: 'status',
      render: (value, record) => (
        <Switch
          checked={!!value}
          checkedChildren="Yes"
          unCheckedChildren="No"
          onChange={() => handleActiveStatus(record.id)}
        />
      ),
    },
    {
      title: 'Actions',
      render: (_, record) => (
        <Button size="small" onClick={() => handleEdit(record)}>
          Edit
        </Button>
      ),
    },
  ];

  // ------------ EXPANDED ROW ----------------
  const expandedRowRender = (rule) => (
    <Collapse activeKey={openRow === rule.id ? ['1'] : []}>
      <Panel header="Conditions" key="1">
        <Table
          size="small"
          pagination={false}
          columns={[
            { title: 'Attribute', dataIndex: 'attributeName' },
            { title: 'Match Type', dataIndex: 'matchType' },
            { title: 'Threshold', dataIndex: 'threshold' },
            { title: 'Weight', dataIndex: 'weight' },
            { title: 'Algorithm', dataIndex: 'algorithm' },
          ]}
          dataSource={rule.ruleCondition}
          rowKey={(r, idx) => idx}
        />
      </Panel>
    </Collapse>
  );

  return (
    <>
      <Card
        title="Match Rules"
        extra={
          <Button type="primary" icon={<PlusOutlined />} size="small" onClick={handleCreateNew}>
            Create New Rule
          </Button>
        }
      >
        <Table
          rowKey="id"
          loading={loading}
          columns={columns}
          dataSource={matchRulesData}
          expandable={{
            expandedRowRender,
            expandedRowKeys: openRow ? [openRow] : [],
            onExpand: (_, record) => toggleRow(record.id),
          }}
          pagination={false}
        />
      </Card>

      {/* MODAL */}
      <Modal open={open} width="50vw" centered footer={null} onCancel={() => setOpen(false)} destroyOnClose>
        <MatchandMergeDetails
          entityId={entityId}
          attributes={attributes}
          editingData={editingData}
          handleClose={() => {
            setOpen(false);
            fetchMatchRules();
          }}
        />
      </Modal>

      {/* {loading && <LoadingIcon />} */}
    </>
  );
};

export default MatchandMerge;
